/** The driver's own laps: the richest screen in the portal, and the only one
 *  that is about one person rather than about the map.
 *
 * Everything here is private by default. The session list, the lap table and
 * both drawings are the visitor's own driving, and the single place another
 * account appears is the "compare with" control — which offers exactly the
 * drivers `GET /api/sessions/public` returns, being the ones who marked a
 * session on this circuit public. There is no way to reach anything else,
 * because there is no endpoint that would answer.
 *
 * The screen is built around one rule about cost: timings are cheap and lap
 * documents are not. `GET /api/sessions/{id}` answers every lap's times in a
 * few kilobytes, which is what the table, the four summary figures and the
 * whole of `components/sessions/derive.ts` work from; the 60 Hz recording
 * behind a lap is megabytes, so it is fetched only for the lap being looked at
 * and the lap being compared against, and cached for as long as the page
 * lives. Clicking down the table therefore costs one request per lap, once.
 *
 * Nothing above the table is stored anywhere. The best lap, the theoretical
 * lap and the spread are derived in the browser from the lap list, which keeps
 * the judgements about which laps count in one tested module instead of in a
 * SQL aggregate nobody can see. The service's own `best_lap_ms` uses the same
 * test, so the two agree without either having to ask the other.
 *
 * The comparison is derived here too. Once the chosen lap and the reference
 * are both on screen, `lib/analysis` — the logger's own arithmetic, ported —
 * places the circuit's corners on the reference lap, lines the chosen lap up
 * with it by where it was on the road, and works out the time delta along
 * the lap and the figures through every corner. The service is never asked:
 * it stores recordings and serves them back, and a Worker parsing two
 * megabytes of samples to draw a chart would be the wrong machine for the
 * job. The cards under the table draw what the comparison answers.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  EmptyState,
  ErrorNote,
  Kicker,
  LoadingPanel,
  Page,
  Segmented,
  Spinner,
} from '@/components/ui';
import { listPublicLaps, listSessions, patchSession } from '@/lib/api';
import { sessionWhen, shortDate } from '@/lib/format';
import type { DriveSession, PublicDriver } from '@/lib/types';
import {
  bestLap,
  consistencyLaps,
  countingLaps,
  consistencyMs,
  sectorBests,
  sectorDeltasMs,
  syncState,
  theoreticalMs,
} from '@/components/sessions/derive';
import { lapLine, peakSpeed, sectorDistances, speedTrace } from '@/components/sessions/geometry';
import type { SpeedTrace } from '@/components/sessions/geometry';
import { referenceOptions, resolveReference } from '@/components/sessions/references';
import { ReferencePath, alignToReference, compareLaps, speedDeviation } from '@/lib/analysis';
import {
  CHOSEN,
  REFERENCE,
  cornerCompareRows,
  gapAtLineMs,
  ggPoints,
  sharedDistance,
} from '@/components/sessions/compare';
import type { Comparison, Deviation } from '@/components/sessions/compare';
import { ConsistencyCard } from '@/components/sessions/ConsistencyCard';
import { CornersCard } from '@/components/sessions/CornersCard';
import { DeltaCard } from '@/components/sessions/DeltaCard';
import { GGCard } from '@/components/sessions/GGCard';
import { InputsCard } from '@/components/sessions/InputsCard';
import {
  forgetSession,
  useLapDocument,
  useLapDocuments,
  useReferenceLap,
  useSessionAnalysis,
  useSessionDetail,
} from '@/components/sessions/useLapData';
import { useTrackGeometry } from '@/lib/useTrackGeometry';
import { analysisFilename, downloadLaps } from '@/components/sessions/exportLaps';
import { AnalysisCard } from '@/components/sessions/AnalysisCard';
import { LapTable } from '@/components/sessions/LapTable';
import { RacingLineCard } from '@/components/sessions/RacingLineCard';
import { SessionList } from '@/components/sessions/SessionList';
import { ShareDialog } from '@/components/sessions/ShareDialog';
import { shareButtonLabel, visibilityOf } from '@/components/sessions/share';
import { SpeedCard } from '@/components/sessions/SpeedCard';
import { SummaryCards } from '@/components/sessions/SummaryCards';

/** Enough to cover a season of evenings without a second page; the listing is
 *  ordered newest first, so what is cut off is what nobody scrolls to. */
const SESSION_LIMIT = 100;

/** The channels the comparison resamples onto the shared axis: what the
 *  cards draw. The corner figures read the raw recording and need none of
 *  these; the fewer there are, the quicker the comparison. */
const COMPARE_CHANNELS = ['t', 'pos_x', 'pos_z', 'speed', 'throttle', 'brake', 'acc_lat', 'acc_long'];

/** Metres between points of the resampled series, the logger's default. */
const COMPARE_STEP_M = 5;

/** The laps the consistency band is drawn over: the logger's default. */
const CONSISTENCY_LAPS = 5;

/** A resampled series as the speed chart reads a trace. */
function alignedTrace(series: Record<string, number[]> | undefined): SpeedTrace | null {
  const distance = series?.['dist'];
  const speed = series?.['speed'];
  if (!distance || !speed || distance.length < 2) return null;
  return { distance, speed };
}

export default function Sessions() {
  const [sessions, setSessions] = useState<DriveSession[]>([]);
  const [listing, setListing] = useState<{ loading: boolean; error: unknown }>({
    loading: true,
    error: null,
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedLap, setSelectedLap] = useState<number | null>(null);
  const [referenceId, setReferenceId] = useState('my_best');
  const [drivers, setDrivers] = useState<PublicDriver[]>([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [keeping, setKeeping] = useState(false);
  const [shareError, setShareError] = useState<unknown>(null);
  const [consistencyWanted, setConsistencyWanted] = useState(false);

  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  // ── the list ──────────────────────────────────────────────────────────────

  const loadSessions = useCallback(async () => {
    setListing({ loading: true, error: null });
    try {
      const reply = await listSessions({ limit: SESSION_LIMIT });
      if (!live.current) return;
      setSessions(reply.sessions);
      setListing({ loading: false, error: null });
      setSelectedId((current) =>
        current && reply.sessions.some((session) => session.id === current)
          ? current
          : (reply.sessions[0]?.id ?? null),
      );
    } catch (error) {
      if (!live.current) return;
      setListing({ loading: false, error });
    }
  }, []);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  // ── the chosen session ────────────────────────────────────────────────────

  const detail = useSessionDetail(selectedId);
  const laps = useMemo(() => detail.value?.laps ?? [], [detail.value]);
  const session =
    sessions.find((row) => row.id === selectedId) ?? detail.value?.session ?? null;

  const best = useMemo(() => bestLap(laps), [laps]);
  const bests = useMemo(() => sectorBests(laps), [laps]);

  // A fresh session opens on its best lap, which is the lap anybody would look
  // at first; a session with none opens on whichever lap it has.
  useEffect(() => {
    if (!detail.value) return;
    setSelectedLap(best?.lap ?? detail.value.laps[0]?.lap ?? null);
  }, [detail.value, best]);

  const selected = laps.find((lap) => lap.lap === selectedLap) ?? null;

  // The logger's analysis of the stint, read once per session and only when
  // the session says one was sent.
  const hasAnalysis = Boolean(detail.value?.analysis);
  const analysis = useSessionAnalysis(selectedId, hasAnalysis);

  // ── the circuit, and who else has driven it ───────────────────────────────

  const officialId = session?.official_id ?? '';
  // The published outline the racing line is drawn on. Public, cached, and
  // keyed by layout rather than by session, so it says nothing about the drive.
  const geometry = useTrackGeometry(officialId);

  useEffect(() => {
    if (!officialId) {
      setDrivers([]);
      return;
    }
    let current = true;
    listPublicLaps(officialId).then(
      (reply) => {
        if (current) setDrivers(reply.drivers);
      },
      () => {
        // A comparison nobody asked for yet is not worth an error on the page:
        // the control simply offers your own best and nothing else.
        if (current) setDrivers([]);
      },
    );
    return () => {
      current = false;
    };
  }, [officialId]);

  const options = useMemo(
    () => referenceOptions(best?.lap_time_ms ?? null, drivers),
    [best, drivers],
  );
  const reference = resolveReference(options, referenceId);

  // A circuit with different drivers on it cannot keep the previous choice.
  useEffect(() => {
    setReferenceId((current) =>
      options.some((option) => option.id === current) ? current : 'my_best',
    );
  }, [options]);

  // ── the two lap documents on screen ───────────────────────────────────────

  // A lap whose recording has gone under the lap retention is not fetched:
  // the row says so, and the page says so in place of the charts.
  const recordingGone = selected !== null && selected.stored === false;
  const chosen = useLapDocument(selectedId, selectedLap, !recordingGone);
  const against = useReferenceLap(reference, selectedId, laps);

  const points = useMemo(() => lapLine(chosen.value), [chosen.value]);
  const referencePoints = useMemo(() => lapLine(against.document), [against.document]);
  const trace = useMemo(() => speedTrace(chosen.value), [chosen.value]);
  const referenceTrace = useMemo(() => speedTrace(against.document), [against.document]);
  const boundaries = useMemo(() => sectorDistances(chosen.value, trace), [chosen.value, trace]);

  const deltas = useMemo(
    () => sectorDeltasMs(selected?.sector_ms ?? [], against.lap?.sector_ms ?? []),
    [selected, against.lap],
  );

  // ── the comparison ────────────────────────────────────────────────────────

  // The reference is "my best", and the chosen lap is it: there is nothing
  // to compare, only the reference's own corners and figures to show.
  const referenceSessionId = reference?.driver?.session_id ?? selectedId;
  const isReference =
    referenceSessionId === selectedId && against.lap !== null && against.lap.lap === selectedLap;

  // The circuit's corners as the port reads them: a position for the apex
  // and, where somebody marked them, the entry and exit.
  const authoredCorners = useMemo(
    () =>
      (geometry?.corners ?? []).map((corner) => ({
        n: corner.n,
        name: corner.name,
        direction: corner.direction,
        apex: corner.apex,
        entry: corner.entry,
        exit: corner.exit,
      })),
    [geometry],
  );
  const comparison = useMemo<Comparison | null>(() => {
    const own = chosen.value?.lap;
    const theirs = against.document?.lap;
    if (!own || !theirs) return null;
    // The port keys its inputs by lap id; the ids here are the two roles.
    const samples = new Map<number, Record<string, number[]>>([[REFERENCE, theirs.samples]]);
    const events = new Map([[REFERENCE, (theirs.events ?? []).map((event) => ({ ...event }))]]);
    if (!isReference) {
      samples.set(CHOSEN, own.samples);
      events.set(CHOSEN, (own.events ?? []).map((event) => ({ ...event })));
    }
    try {
      return compareLaps(samples, events, REFERENCE, COMPARE_CHANNELS, COMPARE_STEP_M, authoredCorners);
    } catch {
      // A recording the port cannot read — no distance axis, say — is a lap
      // with nothing to compare, not a page that fails to draw.
      return null;
    }
  }, [chosen.value, against.document, isReference, authoredCorners]);

  const referenceLap = comparison?.laps[String(REFERENCE)] ?? null;
  const chosenLap = isReference ? referenceLap : (comparison?.laps[String(CHOSEN)] ?? null);

  // The session's quickest counting laps, for the consistency band. Fetched
  // only once asked for, and forgotten with the session.
  const quickest = useMemo(
    () =>
      countingLaps(laps)
        .sort((a, b) => (a.lap_time_ms ?? 0) - (b.lap_time_ms ?? 0))
        .slice(0, CONSISTENCY_LAPS),
    [laps],
  );
  const quickestNumbers = useMemo(() => quickest.map((lap) => lap.lap), [quickest]);
  useEffect(() => setConsistencyWanted(false), [selectedId]);
  const stint = useLapDocuments(selectedId, quickestNumbers, consistencyWanted);
  const deviation = useMemo<Deviation | null>(() => {
    if (!stint.value) return null;
    const samples = quickestNumbers
      .map((lap) => stint.value?.get(lap)?.lap.samples)
      .filter((series): series is Record<string, number[]> => series !== undefined);
    if (samples.length < 2) return null;
    // Lined up on the fastest, as the logger's deviation endpoint does, so a
    // spread is a spread at one place on the road.
    const fastest = samples[0] as Record<string, number[]>;
    const path = new ReferencePath(fastest);
    const lined = [fastest, ...samples.slice(1).map((lap) => alignToReference(lap, path) ?? lap)];
    return speedDeviation(lined, COMPARE_STEP_M);
  }, [stint.value, quickestNumbers]);
  const corners = useMemo(() => referenceLap?.corners ?? [], [referenceLap]);
  const cornerRows = useMemo(
    () => cornerCompareRows(corners, chosenLap, referenceLap, isReference),
    [corners, chosenLap, referenceLap, isReference],
  );
  const gg = useMemo(() => ggPoints(chosenLap, comparison?.accel), [chosenLap, comparison]);
  const axisDistance = sharedDistance([chosenLap, referenceLap]);
  // The gap the timing table would give, not the trace's last point, which
  // ends wherever the shorter recording does.
  const atLineMs = isReference
    ? null
    : gapAtLineMs(selected?.lap_time_ms, against.lap?.lap_time_ms, chosenLap?.delta);
  // The speed chart draws the lined-up series when there is one, so the two
  // traces sit in register by position rather than by each lap's own odometer.
  const speedShown = alignedTrace(chosenLap?.series) ?? trace;
  const referenceShown = alignedTrace(referenceLap?.series) ?? referenceTrace;

  // ── showing a session to other drivers, and keeping it whole ──────────────

  /** A session as the server now has it, into the list. */
  const replaceSession = useCallback((next: DriveSession) => {
    forgetSession(next.id);
    if (!live.current) return;
    setSessions((rows) => rows.map((row) => (row.id === next.id ? { ...row, ...next } : row)));
  }, []);

  const toggleKept = useCallback(async () => {
    if (!session) return;
    setKeeping(true);
    setShareError(null);
    try {
      const reply = await patchSession(session.id, { keep_laps: !session.keep_laps });
      replaceSession(reply.session);
    } catch (error) {
      if (live.current) setShareError(error);
    } finally {
      if (live.current) setKeeping(false);
    }
  }, [session, replaceSession]);

  // ── what is on the page ───────────────────────────────────────────────────

  if (listing.loading && sessions.length === 0) {
    return (
      <Page max={1400}>
        <LoadingPanel label="loading your sessions" />
      </Page>
    );
  }

  if (listing.error && sessions.length === 0) {
    return (
      <Page max={1400}>
        <h1 style={{ fontSize: 24, margin: '0 0 12px' }}>Sessions</h1>
        <ErrorNote
          error={listing.error}
          title="could not load your sessions"
          onRetry={() => void loadSessions()}
        />
      </Page>
    );
  }

  if (sessions.length === 0) {
    return (
      <Page max={1400}>
        <h1 style={{ fontSize: 24, margin: '0 0 12px' }}>Sessions</h1>
        <EmptyState
          title="No sessions yet"
          action={
            <Link className="btn btn-primary" to="/profile/tokens">
              Connect an installation
            </Link>
          }
        >
          Your laps arrive here from the logger. Create a token with the{' '}
          <code>sessions:write</code> scope, paste its connection string into the logger&rsquo;s
          Sync panel, and switch Sessions on — each lap is sent as it ends, and queues locally
          while the service is unreachable.
        </EmptyState>
      </Page>
    );
  }

  const held = detail.value ? laps.length : null;
  const queued = session ? Math.max(0, session.laps - laps.length) : 0;
  const spread = consistencyMs(laps);

  return (
    <Page
      max={1400}
      label="Sessions"
      className="grid gap-6 items-start md:grid-cols-[minmax(260px,300px)_minmax(0,1fr)]"
    >
      <SessionList
        sessions={sessions}
        selectedId={selectedId}
        onSelect={setSelectedId}
        heldLaps={held}
      />

      <section style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <Kicker>
              {session ? `${sessionWhen(session.started_at)} · ${session.car || 'car not recorded'}` : ' '}
            </Kicker>
            <h2 style={{ fontSize: 28, margin: '4px 0 0' }}>
              {session?.track_name || session?.official_id || 'Session'}
            </h2>
          </div>

          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
                minWidth: 0,
                maxWidth: '100%',
              }}
            >
              <span
                style={{ fontSize: 11.5, color: 'var(--color-neutral-500)', whiteSpace: 'nowrap' }}
              >
                compare with
              </span>
              {/* Four drivers do not fit a phone, and a control that pushes the
                  page sideways is worse than one that scrolls inside its own
                  row. The padding and the matching negative margin keep the
                  focus ring from being clipped by the scroll box. */}
              <div
                style={{ overflowX: 'auto', minWidth: 0, maxWidth: '100%', padding: 3, margin: -3 }}
              >
                <Segmented
                  label="compare with"
                  value={reference?.id ?? 'my_best'}
                  onChange={setReferenceId}
                  options={options.map((option) => ({ value: option.id, label: option.label }))}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flex: 'none' }}>
              {/* The whole comparison, on its own screen: the race line,
                  every channel and the rack of panels, opened on the lap
                  chosen here. */}
              {session && laps.length > 0 ? (
                <Link
                  className="btn btn-primary"
                  style={{ fontSize: 12 }}
                  to={`/analysis?session=${encodeURIComponent(session.id)}${
                    selectedLap !== null && selectedLap !== best?.lap ? `&lap=${selectedLap}` : ''
                  }`}
                >
                  Analyse
                </Link>
              ) : null}
              <button
                type="button"
                className="btn btn-secondary"
                style={{ fontSize: 12 }}
                disabled={!session || laps.length === 0}
                onClick={() => {
                  if (session) downloadLaps(session, laps);
                }}
              >
                Export laps
              </button>
              {/* Offered only when the logger sent one; the document is the
                  logger's own format, so it is handed over as it is stored.
                  Worded as a download, so it is not mistaken for "Analyse",
                  which opens the Analysis page. */}
              {session && detail.value?.analysis ? (
                <a
                  className="btn btn-secondary"
                  style={{ fontSize: 12 }}
                  href={`/api/sessions/${encodeURIComponent(session.id)}/analysis`}
                  download={analysisFilename(session)}
                  title="The analysis document the logger sent, as it was stored"
                >
                  Download analysis
                </a>
              ) : null}
              {/* The lap retention removes the recordings of laps that are
                  not the session's best; this asks for all of them to stay.
                  A shared session keeps them anyway, so the button is only
                  offered while the session is private. */}
              {session && visibilityOf(session) === 'private' ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ fontSize: 12 }}
                  disabled={keeping}
                  title={
                    session.keep_laps
                      ? 'Every lap’s recording is kept for as long as the session. Click to let the lap retention apply again.'
                      : 'Keep every lap’s recording for as long as the session, not just the best lap’s.'
                  }
                  onClick={() => void toggleKept()}
                >
                  {keeping ? <Spinner size={12} /> : null}
                  {session.keep_laps ? 'Keeping all laps' : 'Keep all laps'}
                </button>
              ) : null}
              <button
                type="button"
                className="btn btn-secondary"
                style={{ fontSize: 12 }}
                disabled={!session}
                aria-haspopup="dialog"
                onClick={() => setShareOpen(true)}
              >
                {shareButtonLabel(session ? visibilityOf(session) : 'private')}
              </button>
            </div>
          </div>
        </div>

        {shareOpen && session ? (
          <ShareDialog
            session={session}
            bestLapMs={best?.lap_time_ms ?? session.best_lap_ms}
            onChanged={replaceSession}
            onClose={() => setShareOpen(false)}
          />
        ) : null}

        {shareError ? (
          <ErrorNote compact error={shareError} title="could not change this session" />
        ) : null}

        {detail.error ? (
          <ErrorNote
            error={detail.error}
            title="could not load this session"
            onRetry={detail.reload}
          />
        ) : null}

        <SummaryCards
          bestMs={best?.lap_time_ms ?? null}
          bestLapNumber={best?.lap ?? null}
          theoreticalMs={theoreticalMs(laps)}
          consistencyMs={spread}
          consistencyOver={consistencyLaps(laps).length}
          laps={session?.laps ?? laps.length}
          sync={session ? syncState(session, held) : { label: 'no session', tone: 'muted' }}
        />

        {detail.loading && laps.length === 0 ? (
          <LoadingPanel label="loading laps" />
        ) : laps.length === 0 && !detail.error ? (
          <EmptyState title="No laps have arrived for this session" compact>
            {session?.ended_at
              ? 'The drive was recorded but no lap documents reached the service. The logger keeps them until it can send them.'
              : 'This drive is still running. Laps appear here as each one ends.'}
          </EmptyState>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))',
              gap: 18,
              alignItems: 'start',
            }}
          >
            <LapTable
              laps={laps}
              sectorBests={bests}
              bestMs={best?.lap_time_ms ?? null}
              selectedLap={selectedLap}
              onSelect={setSelectedLap}
              queued={queued}
            />

            {recordingGone ? (
              <EmptyState title="This lap’s recording has been removed" compact>
                {selected?.purged_at
                  ? `It went on ${shortDate(selected.purged_at)} under the lap retention. `
                  : 'It went under the lap retention. '}
                The lap’s times are in the table, and the session’s best lap keeps its recording.
                Shared sessions, and sessions kept with <b>Keep all laps</b>, keep every recording.
              </EmptyState>
            ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
              <RacingLineCard
                lapNumber={selectedLap}
                document={chosen.value}
                points={points}
                referencePoints={referencePoints}
                reference={reference}
                referenceMs={against.lap?.lap_time_ms ?? null}
                sectorDeltas={deltas}
                geometry={geometry}
                loading={chosen.loading || against.loading}
                error={chosen.error ?? against.error}
                onRetry={() => {
                  chosen.reload();
                  against.reload();
                }}
              />
              <SpeedCard
                lapNumber={selectedLap}
                trace={speedShown}
                referenceTrace={referenceShown}
                reference={reference}
                sectorDistances={boundaries}
                corners={corners}
                peakKmh={peakSpeed(trace)}
                loading={chosen.loading}
                error={chosen.error}
                onRetry={chosen.reload}
              />
            </div>
            )}
          </div>
        )}

        {laps.length > 0 && !recordingGone ? (
          <>
            <DeltaCard
              lapNumber={selectedLap}
              reference={reference}
              delta={chosenLap?.delta ?? null}
              isReference={isReference}
              aligned={chosenLap?.aligned ?? true}
              corners={corners}
              sectorDistances={boundaries}
              loading={chosen.loading || against.loading}
              atLineMs={atLineMs}
            />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))',
                gap: 18,
                alignItems: 'start',
              }}
            >
              <CornersCard
                lapNumber={selectedLap}
                reference={reference}
                rows={cornerRows}
                isReference={isReference}
                aligned={chosenLap?.aligned ?? true}
                authored={corners.some((corner) => corner.authored === true)}
                atLineMs={atLineMs}
                loading={chosen.loading || against.loading}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
                <InputsCard
                  lapNumber={selectedLap}
                  reference={reference}
                  chosen={chosenLap}
                  against={referenceLap}
                  isReference={isReference}
                  maxDistance={axisDistance}
                  corners={corners}
                  loading={chosen.loading || against.loading}
                />
                <GGCard lapNumber={selectedLap} points={gg} extremes={chosenLap?.gg} />
              </div>
            </div>
            <ConsistencyCard
              laps={quickest}
              data={deviation}
              corners={corners}
              wanted={consistencyWanted}
              loading={stint.loading}
              error={stint.error}
              onLoad={() => setConsistencyWanted(true)}
              onRetry={stint.reload}
            />
          </>
        ) : null}

        {hasAnalysis && laps.length > 0 ? (
          <AnalysisCard
            lapNumber={selectedLap}
            document={analysis.value}
            loading={analysis.loading}
            error={analysis.error}
            onRetry={analysis.reload}
          />
        ) : null}
      </section>
    </Page>
  );
}
